import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  FormLabel,
  ModalError,
  ModalSuccess,
  PasswordInput,
  NumberInput,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  AutoCompleteWithSearch,
} from "components";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { getErrors, validateEmail } from "utils";

const ModalFormUserComposer = ({
  open,
  onClose,
  preload,
  getData,
  optionComposer,
  optionRole,
  filterParams,
  setFilterParams,
  handleChangeFilterParams,
  hasMore,
  loadingFilter,
}) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [payload, setPayload] = useState({
    name: "",
    email: "",
    phone: "",
    role_id: "",
    composer_id: "",
    publisher_id: null,
    ...(!preload && {
      password: "",
      confirm_password: "",
    }),
  });

  const filterSameKey = optionComposer?.filter(
    (item, index, self) =>
      index === self.findIndex(t => t.composer_id === item.composer_id)
  );
  const filteredOptions = filterSameKey?.filter(item =>
    item?.sure_name
      ?.toLowerCase()
      ?.includes(filterParams?.search?.toLowerCase())
  );

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
    if (!preload && (key === "password" || key === "confirm_password")) {
      setPasswordError(null);
    }
    if (key === "email") {
      setEmailError(null);
    }
  };
  const getPreload = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/user/${preload?.user_id}`, {
        headers,
      });
      const { data } = res?.data;
      const requiredKeys = ["name", "composer_id", "role_id", "phone", "email"];
      const filteredData = requiredKeys?.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});
      setPayload(filteredData);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingPage(false);
    }
  };

  const handleSubmit = async () => {
    const method = preload ? axios.put : axios.post;
    const { password, confirm_password, email } = payload;
    let hasError = false;
    setPasswordError(null);
    setEmailError(null);

    if (!preload && password !== confirm_password) {
      setPasswordError("Password and Confirm Password does not match");
      hasError = true;
    }

    if (!validateEmail(email)) {
      setEmailError(
        "Invalid email, please enter your email address in format: yourname@example.com"
      );
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/user${preload ? `/${preload?.user_id}` : ""}`,
        payload,
        { headers }
      );
      onClose();
      ModalSuccess(
        `Successfully ${preload ? "Update" : "Create"} User Composer`
      ).then(() => getData());
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    if (preload) {
      getPreload();
    } else {
      setLoadingPage(false);
    }
  }, [preload]);

  return (
    <Dialog fullWidth open={open} onClose={!loadingPage && onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Add"} User Composer`}</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Object.keys(payload)?.map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={1}>
            {MockFieldForm({
              preload,
              passwordError,
              emailError,
              optionComposer,
              optionRole,
            })?.map(
              (
                { title, key, type, error, options, optionLabel, required },
                index
              ) => (
                <Grid item key={index}>
                  <FormLabel label={title} required={required} />
                  {key === "composer_id" ? (
                    <AutoCompleteWithSearch
                      options={filteredOptions}
                      placeholder="All Composer"
                      uniqueKey="id"
                      value={
                        options?.find(
                          option => option?.id === Number(payload?.composer_id)
                        ) || null
                      }
                      onInputChange={value => {
                        console.log({ searchVal: value });
                        handleChangeFilterParams(value, "search");
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.composer_id === value?.composer_id ||
                        option?.id === value?.id
                      }
                      inputValue={filterParams?.search}
                      onChange={value =>
                        handleChangePayload(value, "composer_id")
                      }
                      setQueryParams={setFilterParams}
                      optionLabel="sure_name"
                      hasMore={hasMore}
                      loading={loadingFilter}
                      width="100%"
                    />
                  ) : type === "autocomplete" ? (
                    <AutoCompleteComponent
                      placeholder={title}
                      options={options}
                      value={
                        options?.find(
                          option => option?.id === Number(payload?.[key])
                        ) || null
                      }
                      onChange={value => handleChangePayload(value, key)}
                      width="100%"
                      size="small"
                      renderOption={(props, option) => {
                        return (
                          <Box component="li" {...props} key={option?.[key]}>
                            {option?.[optionLabel]}
                          </Box>
                        );
                      }}
                    />
                  ) : type === "password" ? (
                    <PasswordInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error}
                      disabled={loadingButton}
                    />
                  ) : type === "phone" ? (
                    <NumberInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e, key)}
                      disabled={loadingButton}
                    />
                  ) : (
                    <TextInput
                      placeholder={title}
                      value={payload?.[key]}
                      autoComplete={`new-${key}`}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error}
                      disabled={loadingButton}
                    />
                  )}
                </Grid>
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          onClick={onClose}
          disabled={loadingPage || loadingButton}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = ({
  preload,
  passwordError,
  emailError,
  optionComposer,
  optionRole,
}) => [
  {
    title: "User Name",
    key: "name",
    required: true,
  },
  {
    title: "Composer",
    key: "composer_id",
    type: "autocomplete",
    options: optionComposer,
    optionLabel: "sure_name",
  },
  {
    title: "Role",
    key: "role_id",
    type: "autocomplete",
    options: optionRole,
    optionLabel: "name",
    required: true,
  },
  ...(preload
    ? []
    : [
        {
          title: "Password",
          key: "password",
          type: "password",
          error: passwordError,
        },
        {
          title: "Confirm Password",
          key: "confirm_password",
          type: "password",
          error: passwordError,
        },
      ]),
  {
    title: "Phone Number",
    key: "phone",
    type: "phone",
    required: true,
  },
  {
    title: "Email",
    key: "email",
    error: emailError,
    required: true,
  },
];
export default ModalFormUserComposer;
